import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '../api/client-api';
import { clientKeys, clientQueries } from '../api/client-queries';
import { buildClientsListFilter, type ClientsListFilter } from '../lib/filters';
import type { ClientListSearch } from './list-params';
import type { ClientType } from './schema';

/** Segment list (vendors-with-orders / clients / vendor-employees). */
export function useLegacyClients(
  clientType: ClientType,
  filter: ClientsListFilter,
  enabled = true,
) {
  return useQuery(clientQueries.list(clientType, filter, enabled));
}

/** Parent tariffs for the filter select. */
export function useLegacyTariffs(enabled = true) {
  return useQuery(clientQueries.tariffs(enabled));
}

/** B2B admins for the sales-manager picker (`roles` is the wire CSV). */
export function useLegacyAdmins(roles: string, enabled = true) {
  return useQuery(clientQueries.admins(roles, enabled));
}

export interface LegacyCounts {
  all_vendors: number;
  businesses: number;
  vendors: number;
  employees: number;
  clients: number;
}

/**
 * Tab-badge counts (cpanel `useClientsPage`): four unfiltered `limit:1`
 * probes plus the businesses count, which DOES follow the current filters.
 * Legacy quirk kept: while a business status filter is active, only the
 * businesses count refetches — the other badges keep their last values
 * because their query keys exclude the status-bearing filter.
 */
export function useLegacyClientCounts(values: Partial<ClientListSearch>, enabled = true) {
  const businessFilter = useMemo(
    () =>
      buildClientsListFilter({
        values: { ...values, cpage: 1, cpageSize: 10 },
        clientType: 'business-vendor',
        businessType: 'ALL',
        sort: null,
      }),
    // Only the filter-bearing params move the businesses count (legacy deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      values.cfrom,
      values.ctill,
      values.csearch,
      values.oc_from,
      values.oc_till,
      values.op_from,
      values.op_till,
      values.bal_from,
      values.bal_till,
      values.ctariff,
      values.cmanager,
      values.cstatus,
    ],
  );

  const countsQuery = useQuery({
    queryKey: clientKeys.counts(businessFilter),
    enabled,
    queryFn: async (): Promise<LegacyCounts> => {
      // Unfiltered probes (legacy sent bare `{limit:1, skip:0}`); the orders
      // window rides along only because the filter type requires it — it
      // never changes a row count.
      const probe: ClientsListFilter = {
        limit: 1,
        skip: 0,
        orders_from_date: businessFilter.orders_from_date,
        orders_till_date: businessFilter.orders_till_date,
      };
      const [vendors, clients, businesses, allVendors, employees] =
        await Promise.allSettled([
          clientApi.vendorsWithOrders({ ...probe, is_business: false }),
          clientApi.clients({ ...probe }),
          clientApi.vendorsWithOrders(businessFilter),
          clientApi.vendorsWithOrders({ ...probe }),
          clientApi.employees({ ...probe }),
        ]);
      const countOf = (r: PromiseSettledResult<{ count: number }>) =>
        r.status === 'fulfilled' ? (r.value?.count ?? 0) : 0;
      return {
        vendors: countOf(vendors),
        clients: countOf(clients),
        businesses: countOf(businesses),
        all_vendors: countOf(allVendors),
        employees: countOf(employees),
      };
    },
  });

  return countsQuery;
}
